import React from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  DownIcon,
  SurfaceColor,
  Theme,
  Tile,
  Typography
} from "@carrotfertility/carotene-core"

const TileTitle = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Typography variant="h5" component="p">
    {children}
  </Typography>
)

// body2 text, including nested body1; links match the text color.
const bodySx = (theme: Theme) => ({
  ...theme.typography.body2,
  "& .MuiTypography-body1": theme.typography.body2,
  "& .MuiLink-root, & .MuiLink-root:hover": { color: "inherit", textDecorationColor: "currentcolor" }
})

// collapsible={false}: open, no caret. Stops clicks so it can sit inside a clickable summary.
export const TileAccordion = ({
  id,
  color,
  title,
  summary,
  collapsible = true,
  children
}: {
  id: string
  color: SurfaceColor
  title: React.ReactNode
  summary?: (expanded: boolean) => React.ReactNode
  collapsible?: boolean
  children: React.ReactNode
}): JSX.Element => {
  const [expanded, setExpanded] = React.useState(false)
  return (
    <Tile
      variant="primary"
      color={color}
      onClick={(event) => event.stopPropagation()}
      sx={(theme) => ({ paddingBlock: theme.tokens.spacing.md })}
    >
      {collapsible ? (
        <Accordion
          disableGutters
          expanded={expanded}
          onChange={(_, isExpanded) => setExpanded(isExpanded)}
          sx={{ backgroundColor: "transparent", boxShadow: "none" }}
        >
          <AccordionSummary
            expandIcon={<DownIcon />}
            aria-controls={`${id}-content`}
            id={`${id}-header`}
            // Carotene sets a 64px min height (also when expanded) and content margins.
            sx={{
              padding: 0,
              "&, &.Mui-expanded": { minBlockSize: 0 },
              "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": { margin: 0 }
            }}
          >
            <div>
              <TileTitle>{title}</TileTitle>
              {summary?.(expanded)}
            </div>
          </AccordionSummary>
          <AccordionDetails
            sx={(theme) => ({ padding: 0, paddingBlockStart: theme.tokens.spacing.xs, ...bodySx(theme) })}
          >
            {children}
          </AccordionDetails>
        </Accordion>
      ) : (
        <>
          <TileTitle>{title}</TileTitle>
          <Box id={`${id}-content`} sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.xs, ...bodySx(theme) })}>
            {children}
          </Box>
        </>
      )}
    </Tile>
  )
}
